// Imports => React
import { useEffect, useMemo, useState } from "react";
import { withStore } from "@stores";
import { observer } from "mobx-react-lite";
import styles from "./details.module.scss";

// Imports => Constants
import {
  AcBadge,
  AcIsSet,
  AcSanitize,
  AcSelectBox,
  AcTooltip,
  ICONS,
  SIZES,
  THEMES,
  TYPES,
} from "@opp/ui-kit";

// Imports => Atoms
import {
  AcContainer,
  AcRow,
  AcColumn,
  AcHeading,
  AcAvatar,
  AcButton,
} from "@opp/ui-kit";
import { useTranslation, withTranslation } from "react-i18next";
import {
  useGetSettingsQuery,
  usePostDac7ReportCreateDownloadUrlMutation,
} from "@src/api/openapi/merchantApi";

function downloadURI(uri) {
  const link = document.createElement("a");
  link.href = uri;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

const AcGeneralInfo = ({
  merchant,
  complianceOverviewUrl,
  complianceRequirements,
  hasDac7Report,
  changeBankAccountUrl,
  configurations_kyc,
  t,
}) => {
  const [trigger, { data, isLoading }] =
    usePostDac7ReportCreateDownloadUrlMutation();
  const [isDownloading, setIsDownloading] = useState(false);
  const { data: settings } = useGetSettingsQuery();
  const [selectedYear, setSelectedYear] = useState();
  const { i18n } = useTranslation();
  const link = data?.report_download_url;

  useEffect(() => {
    link && downloadURI(link);
    setIsDownloading(true);
    setTimeout(() => setIsDownloading(false), 3000);
  }, [link, setIsDownloading]);

  const isOnlyRightToWorkUnverified = useMemo(() => {
    const unverified = complianceRequirements?.filter((e) => {
      return e.status === "unverified";
    });
    const unverifiedRTW = complianceRequirements?.filter((e) => {
      return e.status === "unverified" && e.type === "right_to_work.required";
    });
    return unverified?.length <= unverifiedRTW?.length;
  }, [complianceRequirements]);

  const getName = useMemo(() => {
    if (!AcIsSet(merchant)) {
      return null;
    }
    if (!AcIsSet(merchant.name)) {
      return null;
    }

    return AcSanitize(merchant.name);
  }, [merchant]);

  const getEmail = useMemo(() => {
    if (!AcIsSet(merchant)) {
      return null;
    }
    if (!AcIsSet(merchant.email)) {
      return null;
    }

    return merchant.email;
  }, [merchant]);

  const getBadgeThemeColorOverwrite = (status) => {
    switch (status) {
      case "unverified":
        return "h-background-lightred";
      case "pending":
        return "";
      case "verified":
        return "h-background-lightgreen";
    }
  };

  const getBadgeTheme = (status) => {
    switch (status) {
      case "unverified":
        return THEMES.ERROR_LIGHT;
      case "pending":
        return THEMES.INFO;
      case "verified":
        return THEMES.SUCCESS_LIGHT;
    }
  };

  const getTextClassName = (status) => {
    switch (status) {
      case "unverified":
        return "h-text--color-error";
      case "pending":
        return "";
      case "verified":
        return "h-text--color-charlie";
    }
  };

  const isComplianceRequirementBadgeClickable = (cr) => {
    return cr.status === "unverified" && cr.object_redirect_url;
  };

  const getComplianceRequirementGroup = (type) => {
    switch (type) {
      case "business_information.required":
        return "business_information"
      case "company_information.required":
        return "company_information"
      case "contact.phonenumber.required":
      case "contact.phonenumber.verification.required":
        return "phonenumber";
      case "bank_account.required":
      case "bank_account.verification.required":
        return "bank_account";
      case "contact.verification.required":
        return "identification";
      case "coc_extract.required":
      case "coc.required":
        return "coc_extract";
      case "organization_structure.required":
        return "organization_structure";
      case "ubo.required":
      case "ubo.verification.required":
        return "ubo";
      case "tin.required":
      case "tin.verification.required":
        return "tin";
      case "address.required":
      case "address.verification.required":
        return "address";
      case "source_of_funds.required":
        return "source_of_funds";
      case "right_to_work.required":
        return "right_to_work";
    }

    return "unknown";
  };

  const renderComplianceRequirementBadges = useMemo(() => {
    if (!AcIsSet(complianceRequirements)) {
      return null;
    }

    if (configurations_kyc === false) {
      return null;
    }

    let groupsCount = [];

    complianceRequirements = complianceRequirements.filter(
      (complianceRequirement) => {
        return !(
          complianceRequirement.type === "bank_account.required" ||
          complianceRequirement.type === "bank_account.verification.required"
        );
      }
    );

    complianceRequirements.forEach((complianceRequirement) => {
      const group = getComplianceRequirementGroup(complianceRequirement.type);
      if (!(group in groupsCount)) {
        groupsCount[group] = [];
        groupsCount[group]["count"] = 1;
        groupsCount[group]["currentCount"] = 1;
      } else {
        groupsCount[group]["count"] += 1;
      }
    });

    return (
      <AcRow>
        {complianceRequirements.map((complianceRequirement) => {
          const group = getComplianceRequirementGroup(
            complianceRequirement.type
          );

          let currentCount = "";

          if (groupsCount[group]["count"] > 1) {
            currentCount = groupsCount[group]["currentCount"];
            groupsCount[group]["currentCount"] += 1;
          }

          return (
            <AcTooltip
              content={t(
                "compliance-requirement.badge-tooltip." +
                  complianceRequirement.status,
                { ns: "account" }
              )}
              trigger="hover"
              placement="top"
            >
              <div
                onClick={() => {
                  if (
                    isComplianceRequirementBadgeClickable(complianceRequirement)
                  ) {
                    window
                      .open(complianceRequirement.object_redirect_url, "_blank")
                      .focus();
                  }
                }}
              >
                <AcBadge
                  className={`h-margin-right-5 h-margin-top-5 ${getBadgeThemeColorOverwrite(
                    complianceRequirement.status
                  )} ${
                    isComplianceRequirementBadgeClickable(complianceRequirement)
                      ? "h-cursor-pointer"
                      : ""
                  }`}
                  textClassName={getTextClassName(complianceRequirement.status)}
                  theme={getBadgeTheme(complianceRequirement.status)}
                >
                  {t(
                    "account:compliance-requirement.type." + group,
                    t("account:compliance-requirement.type.unknown")
                  )}{" "}
                  {currentCount ? currentCount : ""}
                </AcBadge>
              </div>
            </AcTooltip>
          );
        })}
      </AcRow>
    );
  }, [complianceRequirements]);

  const handleGoToComplianceOverview = (event) => {
    if (event && event.preventDefault) {
      event.preventDefault();
    }
    if (event && event.stopPropagation) {
      event.stopPropagation();
    }

    window.open(complianceOverviewUrl, "_blank").focus();
  };

  const getGoToComplianceOverviewButtonOptions = useMemo(() => {
    return {
      type: TYPES.BUTTON,
      theme: THEMES.OMEGA,
      title: `${t(
        "general-info.go-to-compliance-overview",
        "Go to compliance overview",
        { ns: "account" }
      )}`,
      className: "h-margin-top-15",
      callback: handleGoToComplianceOverview,
    };
  }, [handleGoToComplianceOverview]);

  const getDac7Report = useMemo(() => {
    return {
      type: TYPES.BUTTON,
      theme: THEMES.OMEGA,
      size: SIZES.SMALL,
      title: `${t("general-info.download-dac7-report", "Download tax report", {
        ns: "account",
      })}`,
      disabled: !selectedYear || isLoading || isDownloading,
      className: "h-margin-top-15",
      callback: () =>
        trigger({
          body: {
            language: i18n.resolvedLanguage.slice(0, 2),
            reporting_year: selectedYear,
          },
        }),
    };
  }, [trigger, selectedYear, isLoading, isDownloading]);

  return (
    <AcContainer>
      <AcRow>
        <div className={"d-none d-lg-block h-margin-right-15"}>
          <AcColumn lg={3} className={"h-padding-left-0 h-margin-left-0"}>
            <AcAvatar
              avatar={{
                type: TYPES.ICON,
                value:
                  (merchant && merchant.type) === "business"
                    ? ICONS.OPP_USER_BUSINESS
                    : ICONS.OPP_USER_INDIVIDUAL,
                size: 12,
              }}
              theme={THEMES.OMEGA}
            />
          </AcColumn>
        </div>

        <AcColumn className={"h-padding-left-15"}>
          <AcRow className={"h-margin-top-25"}>
            <AcHeading
              rank={3}
              theme={THEMES.ECHO_LIGHT}
              className={"h-margin-bottom-5 h-bold-heading"}
            >
              {getName}
            </AcHeading>
          </AcRow>

          {getEmail && (
            <AcRow>
              <span
                className={"h-text--color-medium h-margin-bottom-5"}
                style={{ wordBreak: "break-all" }}
              >
                {getEmail}
              </span>
            </AcRow>
          )}

          {renderComplianceRequirementBadges}

          <AcRow
            style={{ gap: "0.5rem" }}
            className={"h-margin-bottom-25 h-margin-top-5 gap-1"}
          >
            {configurations_kyc && !isOnlyRightToWorkUnverified && (
              <AcButton
                {...getGoToComplianceOverviewButtonOptions}
                tag={"a"}
                href={complianceOverviewUrl}
              >
                <span>
                  {t(
                    "general-info.compliance-overview",
                    "Compliance overview",
                    {
                      ns: "account",
                    }
                  )}
                </span>
              </AcButton>
            )}
            {hasDac7Report && (
              <div className={styles.container}>
                {settings?.data ? (
                  <AcSelectBox
                    name="year"
                    label={t("general-info.download-dac7-label", {
                      ns: "account",
                    })}
                    placeholder={t("general-info.download-dac7-placeholder")}
                    type={TYPES.TEXT}
                    value={selectedYear}
                    callback={(e, _, v) => setSelectedYear(v)}
                    options={settings?.data?.dac7_report_years.map((e) => {
                      return {
                        name: e.toString(),
                        value: e.toString(),
                      };
                    })}
                  />
                ) : null}
                <AcButton {...getDac7Report}>
                  <span>
                    {t(
                      "general-info.download-dac7-report",
                      "Download tax report",
                      {
                        ns: "account",
                      }
                    )}
                  </span>
                </AcButton>
              </div>
            )}
            {changeBankAccountUrl && configurations_kyc && (
              <a href={changeBankAccountUrl} target="_blank" rel="noreferrer">
                <AcButton
                  type={TYPES.BUTTON}
                  theme={THEMES.OMEGA}
                  title={`${t("general-info.change-bank-account", {
                    ns: "account",
                  })}`}
                  className="h-margin-top-15"
                  loading={isLoading}
                >
                  <span>
                    {t("general-info.change-bank-account", {
                      ns: "account",
                    })}
                  </span>
                </AcButton>
              </a>
            )}
          </AcRow>
        </AcColumn>
      </AcRow>
    </AcContainer>
  );
};

export default withTranslation(["account"])(withStore(observer(AcGeneralInfo)));
